import L from 'leaflet';

export interface CanvasOverlayOptions extends L.LayerOptions {
  paneName?: string;
  zIndex?: number;
}

/**
 * Custom Leaflet layer that manages a canvas element synced to the map
 * viewport. Provides an onDraw callback for rendering. Ported verbatim from
 * sites/my-fdi/src/layers/CanvasOverlay.ts (pure Leaflet, no app deps).
 */
export class CanvasOverlayLayer extends L.Layer {
  _canvas: HTMLCanvasElement | null = null;
  _ctx: CanvasRenderingContext2D | null = null;
  _paneName: string;
  _zIndex: number;
  onDraw: ((ctx: CanvasRenderingContext2D, map: L.Map, size: L.Point) => void) | null = null;

  constructor(options?: CanvasOverlayOptions) {
    super(options);
    this._paneName = options?.paneName ?? 'overlayPane';
    this._zIndex = options?.zIndex ?? 400;
  }

  override onAdd(map: L.Map): this {
    let pane = map.getPane(this._paneName);
    if (!pane) {
      pane = map.createPane(this._paneName);
      pane.style.zIndex = String(this._zIndex);
    }

    this._canvas = L.DomUtil.create('canvas', 'leaflet-canvas-overlay', pane);
    this._ctx = this._canvas.getContext('2d');
    this._canvas.style.pointerEvents = 'none';

    map.on('moveend zoomend resize', this._reset, this);
    this._reset();
    return this;
  }

  override onRemove(map: L.Map): this {
    if (this._canvas) {
      L.DomUtil.remove(this._canvas);
      this._canvas = null;
      this._ctx = null;
    }
    map.off('moveend zoomend resize', this._reset, this);
    return this;
  }

  _reset() {
    const map = this._map;
    if (!map || !this._canvas) return;

    const size = map.getSize();
    this._canvas.width = size.x;
    this._canvas.height = size.y;

    const topLeft = map.containerPointToLayerPoint([0, 0]);
    L.DomUtil.setPosition(this._canvas, topLeft);

    this.redraw();
  }

  redraw() {
    if (this._ctx && this._map) {
      const size = this._map.getSize();
      this.onDraw?.(this._ctx, this._map, size);
    }
  }

  getCanvas(): HTMLCanvasElement | null {
    return this._canvas;
  }

  getContext(): CanvasRenderingContext2D | null {
    return this._ctx;
  }
}
